import { api, privateApi } from 'api'
import { ITokensResponse } from 'models/auth'
import { ISeller } from 'models/seller'
import {
  IAcceptInvitationRequest,
  IAcceptInvitationResponse,
  IConfirmOwnershipTransferRequest,
  IConfirmSectionAccessResponse,
  IEmployeeUpdateSellerRequest,
  IGetSellerEmployeesQuery,
  IInvitationPhoneCodeResponse,
  IInviteSellerEmployeeRequest,
  INotificationSettingsResponse,
  INotificationSettingsTarget,
  IOpenInvitationResponse,
  IOwnershipCodesResponse,
  IRequestSectionAccessResponse,
  ISellerEmployee,
  ISellerOwner,
  IUpdateSellerEmployeeRequest,
  TEmployeeAccessSection,
  TNotificationSettings,
  TSellerPanelMe
} from 'models/sellerEmployees'
import { getNormalizedFormData } from 'shared/utils/getNormalizedFormData'

export const sellerEmployeesApi = {
  getMe() {
    return privateApi.get<TSellerPanelMe>('seller/employees/me')
  },
  getEmployees(params: IGetSellerEmployeesQuery) {
    return privateApi.get<ISellerEmployee[]>('seller/employees', { params })
  },
  inviteEmployee(data: IInviteSellerEmployeeRequest) {
    return privateApi.post<ISellerEmployee>('seller/employees', data)
  },
  updateEmployee(
    id: string,
    data: IUpdateSellerEmployeeRequest,
    sellerId?: string
  ) {
    return privateApi.patch<ISellerEmployee>(`seller/employees/${id}`, data, {
      params: { seller_id: sellerId }
    })
  },
  fireEmployee(id: string, sellerId?: string) {
    return privateApi.post<ISellerEmployee>(
      `seller/employees/${id}/fire`,
      undefined,
      { params: { seller_id: sellerId } }
    )
  },
  resendInvitation(id: string, sellerId?: string) {
    return privateApi.post<ISellerEmployee>(
      `seller/employees/${id}/invitation/resend`,
      undefined,
      { params: { seller_id: sellerId } }
    )
  },
  revokeInvitation(id: string, sellerId?: string) {
    return privateApi.delete<{ _id: string }>(
      `seller/employees/${id}/invitation`,
      { params: { seller_id: sellerId } }
    )
  },
  getOwner(sellerId?: string) {
    return privateApi.get<ISellerOwner>('seller/employees/owner', {
      params: { seller_id: sellerId }
    })
  },
  requestOwnershipTransfer(id: string) {
    return privateApi.post<IOwnershipCodesResponse>(
      `seller/employees/${id}/ownership/request`
    )
  },
  // В ответе новая сессия прежнего владельца: в кабинете он остаётся сотрудником
  confirmOwnershipTransfer(id: string, data: IConfirmOwnershipTransferRequest) {
    return privateApi.post<ITokensResponse>(
      `seller/employees/${id}/ownership/confirm`,
      data
    )
  },
  // Страница приглашения открыта без входа: доступ даёт токен из ссылки
  openInvitation(token: string) {
    return api.post<IOpenInvitationResponse>(
      'seller/employees/invitation/open',
      { token }
    )
  },
  requestInvitationPhoneCode(token: string, phone: string) {
    return api.post<IInvitationPhoneCodeResponse>(
      'seller/employees/invitation/phone-code',
      { token, phone }
    )
  },
  acceptInvitation(data: IAcceptInvitationRequest) {
    return api.post<IAcceptInvitationResponse>(
      'seller/employees/invitation/accept',
      data
    )
  },
  requestSectionAccess(section: TEmployeeAccessSection) {
    return privateApi.post<IRequestSectionAccessResponse>(
      'seller/employees/access/request',
      { section }
    )
  },
  confirmSectionAccess(section: TEmployeeAccessSection, code: string) {
    return privateApi.post<IConfirmSectionAccessResponse>(
      'seller/employees/access/confirm',
      { section, code }
    )
  },
  // Тот же PATCH, что у владельца: сотрудника бэкенд узнаёт по токену
  updateSeller(data: IEmployeeUpdateSellerRequest) {
    return privateApi.patch<ISeller>(
      'admin/seller',
      getNormalizedFormData(data)
    )
  },
  getNotificationSettings(params: INotificationSettingsTarget) {
    return privateApi.get<INotificationSettingsResponse>(
      'seller/notification-settings',
      { params }
    )
  },
  saveNotificationSettings(
    data: TNotificationSettings,
    params: INotificationSettingsTarget
  ) {
    return privateApi.patch<INotificationSettingsResponse>(
      'seller/notification-settings',
      data,
      { params }
    )
  },
  disconnectBot(
    channel: 'telegram' | 'max',
    params: INotificationSettingsTarget
  ) {
    return privateApi.delete<INotificationSettingsResponse>(
      'seller/notification-settings/bot',
      { params: { ...params, channel } }
    )
  }
}
